import * as types from 'src/store/mutation-types'

import { ItemsClient } from 'src/api/Items'
import { Notify } from 'quasar'
import i18n from 'src/i18n/i18n'

// initial state
// shape: [{ id, quantity }]
const state = {
  items: [],
  itemsMeta: null,
  itemsLoaded: false,
  selectedItem: null
}

// getters
const getters = {
  items: state => state.items,
  itemsMeta: state => state.itemsMeta,
  itemsLoaded: state => state.itemsLoaded,
  selectedItem: state => state.selectedItem
}

const paginationDefault = {
  page: 1,
  rowsNumber: 0,
  rowsPerPage: 15,
  descending: false,
  sortBy: 'item_name'
}

const actions = {
  listItems ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_ITEMS_LOADED, false)

    return new ItemsClient()
      .sort(sort)
      .page(pagination.page)
      .limit(pagination.rowsPerPage)
      .list()
      .then(response => {
        commit(types.SET_ITEMS, response.data.data)
        commit(types.SET_ITEMS_META, response.data.meta)
        commit(types.SET_ITEMS_LOADED, true)
      })
      .then(() => Promise.resolve({ items: state.items, meta: state.itemsMeta }))
  },

  searchItems ({ commit, state }, { pagination, filter }) {
    commit(types.SET_ITEMS_LOADED, false)

    return new ItemsClient()
      .search(filter)
      .then(response => {
        commit(types.SET_ITEMS, response.data.data)
        commit(types.SET_ITEMS_META, {
          pagination: {
            count: response.data.data.length,
            current_page: pagination.page,
            per_page: pagination.rowsPerPage,
            total: response.data.data.length,
            total_pages: 1
          }
        })
        commit(types.SET_ITEMS_LOADED, true)
      })
      .then(() => Promise.resolve({ items: state.items, meta: state.itemsMeta }))
  },

  fetchSelectedItem ({ commit }, itemId) {
    return new ItemsClient()
      .get(itemId)
      .then(response => {
        commit(types.SET_SELECTED_ITEM, response.data.data[0])
      })
  },

  createItem ({ commit }, data) {
    return new ItemsClient()
      .create(data)
      .then(response => {
        let item = response.data.data
        commit(types.ADD_ITEM, item)
      })
  },

  updateItem ({ commit }, { itemId, item }) {
    return new ItemsClient()
      .update(itemId, item)
      .then(response => {
        let item = response.data.data

        commit(types.UPDATE_ITEM, item)

        return item
      })
  },

  deleteItems ({ commit, state }, items) {
    let ids = items.map(item => item.id)

    return new ItemsClient()
      .destroy(ids)
      .then(() => items.forEach(item => commit(types.REMOVE_ITEM, item)))
      .then(() => commit(types.SET_ITEMS_META, {
        pagination: {
          ...state.itemsMeta.pagination,
          total: state.itemsMeta.pagination.total - items.length
        }
      }))
      .then(() => Notify.create({ message: i18n.tc('item_deleted', 2), type: 'positive' }))
      .then(() => Promise.resolve({ items: state.items, meta: state.itemsMeta }))
  }
}

// mutations
const mutations = {
  [types.SET_ITEMS] (state, items) {
    state.items = items
  },

  [types.SET_ITEMS_META] (state, meta) {
    state.itemsMeta = meta
  },

  [types.SET_ITEMS_LOADED] (state, value) {
    state.itemsLoaded = value
  },

  [types.SET_SELECTED_ITEM] (state, item) {
    state.selectedItem = item
  },

  [types.ADD_ITEM] (state, item) {
    state.items.push(item)
  },

  [types.UPDATE_ITEM] (state, newItem) {
    let itemIndex = state.items.findIndex(item => item.id === newItem.id)
    state.items.splice(itemIndex, 1, newItem)
  },

  [types.REMOVE_ITEM] (state, item) {
    const toDelete = state.items.find(stateItem => stateItem.id === item.id)
    const index = state.items.indexOf(toDelete)

    if (index !== -1) {
      state.items.splice(index, 1)
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
